import type { Domain, Profile } from "./types";

export interface SwissTip {
  id: string;
  domain: Domain;
  title: string;
  body: string;
  relevance: string;
  sourceLabel: string;
  sourceUrl: string;
  score: number;
}

interface TipDefinition extends Omit<SwissTip, "relevance" | "score"> {
  applies: (profile: Profile, today: string) => boolean;
  relevance: (profile: Profile) => string;
  score: (profile: Profile) => number;
}

const definitions: TipDefinition[] = [
  {
    id: "source-tax-3a",
    domain: "Tax",
    title: "A 3a payment does not automatically reduce tax at source.",
    body: "Residents taxed at source generally need a subsequent ordinary assessment to claim 3a and other deductions. The request is due by 31 March of the following year and is normally non-extendable.",
    relevance: () => "Your profile says you are taxed at source.",
    sourceLabel: "Federal Tax Administration",
    sourceUrl: "https://www.estv.admin.ch/de/fragen-und-antworten",
    applies: (profile) => profile.taxMode === "source" && profile.livesInSwitzerland === "yes",
    score: () => 100,
  },
  {
    id: "cross-border-tax-route",
    domain: "Tax",
    title: "Cross-border tax is a route to verify, not a Swiss-resident deadline.",
    body: "Your filing and deduction route can depend on your residence country, work canton, working pattern and the applicable agreement. Verify both sides before acting on a resident-only deadline.",
    relevance: (profile) => `Your profile says you live in ${profile.residenceCountry || "another country"} and work in Switzerland.`,
    sourceLabel: "Swiss Confederation - cross-border workers",
    sourceUrl: "https://www.ch.ch/en/work/working-in-switzerland-as-a-foreign-national/",
    applies: (profile) => profile.livesInSwitzerland === "no",
    score: () => 100,
  },
  {
    id: "accident-duplicate",
    domain: "Health",
    title: "Your health premium may still include accident cover you already have.",
    body: "Employees working at least eight hours per week for one employer are normally covered for non-occupational accidents through work. With employer proof, the accident component of basic health insurance can be suspended.",
    relevance: (profile) => `You recorded ${profile.weeklyHours} working hours per week.`,
    sourceLabel: "Federal Office of Public Health",
    sourceUrl: "https://www.bag.admin.ch/en/health-insurance-insured-persons-eligible-to-suspend-accident-cover",
    applies: (profile) => profile.employment === "employed" && (profile.weeklyHours ?? 0) >= 8,
    score: (profile) => profile.accidentInHealthInsurance === "yes" ? 98 : 86,
  },
  {
    id: "vested-benefits-follow-you",
    domain: "Pension",
    title: "Pillar 2 assets do not move to a new job automatically in every case.",
    body: "Your former fund needs the new fund’s details. If no new job starts immediately, you choose a vested-benefits institution; forgotten assets can later be traced through the 2nd Pillar Central Office.",
    relevance: (profile) => profile.employment === "unemployed"
      || profile.employmentEndPlan === "job-seeking-switzerland"
      || profile.employmentEndPlan === "new-swiss-job"
      ? "A job transition is recorded in your profile."
      : "Useful whenever you change employer or pause work.",
    sourceLabel: "Federal Social Insurance Office",
    sourceUrl: "https://www.bsv.admin.ch/dam/en/sd-web/Hq7iVHiNN8oZ/bsv-merkblatt-stellenwechsel-en.pdf",
    applies: (profile) => profile.pensionFundMember !== "no",
    score: (profile) => profile.employment === "unemployed"
      || profile.employmentEndPlan === "job-seeking-switzerland"
      || profile.employmentEndPlan === "new-swiss-job"
      ? 96
      : 52,
  },
  {
    id: "3a-catchup-2025",
    domain: "Pension",
    title: "2026 opens the first limited Pillar 3a catch-up opportunity.",
    body: "An eligible missed 2025 contribution can potentially be made in 2026, but only after fully funding the ordinary 2026 maximum and meeting income conditions in both years.",
    relevance: () => "This is a new rule that many established residents have not encountered before.",
    sourceLabel: "Federal Social Insurance Office",
    sourceUrl: "https://www.bsv.admin.ch/en/newnsb/uvIC0mFBWEozIsXH-rb49",
    applies: (profile, today) =>
      today.startsWith("2026")
      && (profile.employment === "employed" || profile.employment === "self-employed"),
    score: (profile) => (profile.missed3aFor2025 ?? 0) > 0 ? 99 : 74,
  },
  {
    id: "plates-on-pause",
    domain: "Mobility",
    title: "A parked vehicle does not always need active plates.",
    body: "If a car or motorbike will be unused for a while, plates can be deposited with the cantonal road traffic office. The office informs the insurer, and liability premiums and vehicle tax pause while the plates are deposited.",
    relevance: (profile) => profile.vehicle === "both"
      ? "You recorded both a car and a motorbike."
      : `You recorded a ${profile.vehicle}.`,
    sourceLabel: "Swiss Confederation · ch.ch",
    sourceUrl: "https://www.ch.ch/de/fahrzeuge-und-verkehr/autos-und-andere-fahrzeuge/fahrzeug-einlosen/",
    applies: (profile) => Boolean(profile.vehicle && profile.vehicle !== "none"),
    score: (profile) => profile.vehicle === "both" ? 91 : 69,
  },
  {
    id: "supplementary-not-basic",
    domain: "Health",
    title: "Never treat supplementary insurance like basic insurance.",
    body: "A basic insurer must accept eligible applicants, but a supplementary insurer can refuse, restrict or price coverage after health checks. Secure written acceptance before cancelling supplementary cover.",
    relevance: () => "This distinction matters during every insurance comparison.",
    sourceLabel: "Federal Office of Public Health",
    sourceUrl: "https://www.bag.admin.ch/en/premiums-and-costs-answers-to-frequently-asked-questions",
    applies: () => true,
    score: () => 64,
  },
  {
    id: "ahv-statement-free",
    domain: "Pension",
    title: "Your AHV contribution record can be checked for free.",
    body: "The individual-account statement shows recorded contribution periods and income. Comparing it with your work history early gives you more time to document employer posting errors or gaps.",
    relevance: (profile) => profile.ahvStatementDate
      ? `Your latest recorded statement is from ${profile.ahvStatementDate}.`
      : "No AHV statement date is recorded yet.",
    sourceLabel: "OASI/DI Information Centre",
    sourceUrl: "https://www.ahv-iv.ch/en/Forms/Statement-of-the-individual-account",
    applies: () => true,
    score: (profile) => profile.ahvStatementDate ? 40 : 82,
  },
  {
    id: "w8ben-is-not-da1",
    domain: "Tax",
    title: "W-8BEN and DA-1 solve different parts of US dividend withholding.",
    body: "W-8BEN is normally given to the broker or payer to establish foreign status and treaty treatment. DA-1 is the Swiss request for credit of eligible non-refundable foreign withholding on income declared in the tax return.",
    relevance: (profile) => profile.usesUSListedSecurities === "yes"
      ? "You recorded US-listed securities."
      : "Useful before buying a US-distributing security.",
    sourceLabel: "Federal Tax Administration",
    sourceUrl: "https://www.estv.admin.ch/de/usa",
    applies: (profile) => profile.usesUSListedSecurities === "yes",
    score: (profile) => profile.w8BenSignedDate ? 82 : 97,
  },
  {
    id: "broker-total-cost",
    domain: "Money",
    title: "A low commission is not the total cost of a broker.",
    body: "Compare custody, foreign-exchange spread, product costs, tax-statement support and the behaviour encouraged by the interface. A simple recurring process can matter more than a tiny trading-fee difference.",
    relevance: (profile) => profile.brokerStatus === "none"
      ? "You have not recorded a current broker."
      : "Useful during the annual strategy review.",
    sourceLabel: "FINMA investor protection",
    sourceUrl: "https://www.finma.ch/en/finma-public/authorised-institutions-individuals-and-products/",
    applies: (profile) => Boolean(profile.investmentHorizon),
    score: (profile) => profile.brokerStatus === "none" ? 89 : 58,
  },
  {
    id: "private-gains-not-automatic",
    domain: "Tax",
    title: "Tax-free private capital gains are not an unlimited trading exemption.",
    body: "Frequent trading, short holding periods, leverage, derivatives and reliance on trading income can contribute to professional securities-trader treatment. The FTA safe-harbour criteria are cumulative and individual facts matter.",
    relevance: (profile) => profile.usesMarginOrFrequentTrading === "yes"
      ? "You recorded margin use or frequent trading."
      : "Useful before adding leverage, derivatives or a high-turnover strategy.",
    sourceLabel: "Federal Tax Administration · Circular 36",
    sourceUrl: "https://www.estv.admin.ch/dam/de/sd-web/oOz28af293pZ/dbst-ks-2012-1-036-d-de.pdf",
    applies: (profile) => profile.usesMarginOrFrequentTrading === "yes",
    score: () => 100,
  },
  {
    id: "rent-rate-basis",
    domain: "Home",
    title: "The headline reference rate is not enough to judge your rent.",
    body: "The useful comparison is between the current federal rate and the rate in your lease or latest valid rent adjustment. Inflation and operating-cost changes can offset a reduction.",
    relevance: (profile) => profile.leaseReferenceRate === undefined
      ? "You rent, but the reference rate behind your current rent is not recorded."
      : `Your recorded lease basis is ${profile.leaseReferenceRate}%.`,
    sourceLabel: "Federal Housing Office",
    sourceUrl: "https://www.bwo.admin.ch/de/referenzzinssatz",
    applies: (profile) => profile.housing === "renter",
    score: (profile) => profile.leaseReferenceRate === undefined ? 88 : 66,
  },
  {
    id: "permit-c-route",
    domain: "Residency",
    title: "Permit C can follow a five- or ten-year residence route.",
    body: "Nationality, continuous residence, integration and cantonal practice all matter. A simple ‘B permit plus five years’ assumption is not safe for everyone.",
    relevance: (profile) => profile.permitGoal === "permit-c"
      ? "Permit C is your recorded residency goal."
      : `Your profile records permit ${profile.permitType ?? "status not yet specified"}.`,
    sourceLabel: "State Secretariat for Migration",
    sourceUrl: "https://www.sem.admin.ch/sem/en/home/themen/aufenthalt/eu_efta/ausweis_c_eu_efta.html",
    applies: (profile) => profile.residency !== undefined && profile.residency !== "swiss",
    score: (profile) => profile.permitGoal === "permit-c" ? 97 : 63,
  },
  {
    id: "liability-voluntary-important",
    domain: "Protection",
    title: "Personal liability is usually voluntary—and still unusually important.",
    body: "It can protect against large third-party claims and is often requested by landlords. It is different from household-contents insurance, which protects your own belongings.",
    relevance: (profile) => profile.personalLiabilityCovered === "no"
      ? "Your profile records no personal liability cover."
      : "Useful when reviewing a lease or household policy bundle.",
    sourceLabel: "Swiss Confederation · ch.ch",
    sourceUrl: "https://www.ch.ch/de/versicherungen/privathaftpflichtversicherung",
    applies: () => true,
    score: (profile) => profile.personalLiabilityCovered === "no" ? 94 : 46,
  },
];

export function tipsForProfile(profile: Profile, today: string): SwissTip[] {
  return definitions
    .filter((tip) => tip.applies(profile, today))
    .map((tip) => ({
      id: tip.id,
      domain: tip.domain,
      title: tip.title,
      body: tip.body,
      relevance: tip.relevance(profile),
      sourceLabel: tip.sourceLabel,
      sourceUrl: tip.sourceUrl,
      score: tip.score(profile),
    }))
    .sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
}
